import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { REGEX } from 'utils/regex';
import { useIncodeFlow } from './useIncodeFlow';
import { useLineMutations } from './useLineMutations';
import { useLineNavigationState } from './useLineNavigationState';
import { usePersistentLines } from './usePersistentLines';

export const useManageLines = ({
  addLine,
  addLineAction,
  cleanAddLine,
  removeLine,
  removeLineAction,
  cleanRemoveLine,
  createIncodeLinks,
  createIncodeLinksAction,
  cleanCreateIncodeLinks,
  getIncodeStatus,
  getIncodeStatusAction,
  cleanIncodeStatus,
  downloadPdf,
  downloadPdfAction,
  cleanDownloadPdf,
  consultLegalId,
  consultLegalIdAction,
  cleanConsultLegalId,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { navigationState, uuidFromPath, legalId, orderId, storageKey } =
    useLineNavigationState(location);
  const { lines, setLines } = usePersistentLines({ storageKey, navigationState });
  const [dialogTitleMessage, setDialogTitleMessage] = useState('');
  const [dialogMessage, setDialogMessage] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isIncodeStatusDialogLocked, setIsIncodeStatusDialogLocked] = useState(false);
  const [icon, setIcon] = useState();
  const [aditionalInfoDialog, setAditionalInfoDialog] = useState(null);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({});

  const dataValidation = {
    direct_number: {
      required: 'El teléfono es requerido',
      pattern: {
        value: REGEX.phone,
        message: 'El teléfono debe tener 10 digitos',
      },
    },
  };

  const openErrorDialog = useCallback(({ title, message, icon: errorIcon }) => {
    setIcon(errorIcon);
    setDialogTitleMessage(title);
    setDialogMessage(message);
    setIsDialogOpen(true);
  }, []);

  const unlockStatusDialog = useCallback(() => setIsIncodeStatusDialogLocked(false), []);

  const {
    loading,
    addLineForm,
    handleDeleteLine,
    isRemoveDialogOpen,
    handleCloseRemoveDialog,
    handleConfirmRemoveLine,
    pendingRemoveLine,
    formatPhoneNumber,
  } = useLineMutations({
    addLine,
    addLineAction,
    cleanAddLine,
    removeLine,
    removeLineAction,
    cleanRemoveLine,
    legalId,
    orderId,
    setLines,
    setValue,
    onError: openErrorDialog,
    unlockStatusDialog,
  });

  const {
    isIncodeDialogOpen,
    incodeLinksDialog,
    handleCloseIncodeDialog,
    incodeLinksLoading,
    generateIncodeLinks,
    getIncodeStatusExecute,
  } = useIncodeFlow({
    createIncodeLinks,
    createIncodeLinksAction,
    cleanCreateIncodeLinks,
    getIncodeStatus,
    getIncodeStatusAction,
    cleanIncodeStatus,
    legalId,
    orderId,
    uuidFromPath,
    navigateToRfcSearch: () => {
      navigate('/line-assignments/search-orders', {
        state: {
          legal_id: legalId,
          order_id: orderId,
          searchFormData: navigationState?.searchFormData,
        },
      });
    },
    setValue,
    dialog: {
      setIsDialogOpen,
      setIsIncodeStatusDialogLocked,
      setIcon,
      setDialogTitleMessage,
      setDialogMessage,
      setAditionalInfoDialog,
    },
    downloadPdf,
    downloadPdfAction,
    cleanDownloadPdf,
    consultLegalId,
    consultLegalIdAction,
    cleanConsultLegalId,
  });

  const handleClose = () => {
    setIsDialogOpen(false);
    setIsIncodeStatusDialogLocked(false);
    setAditionalInfoDialog(null);
    cleanAddLine();
    cleanCreateIncodeLinks();
    cleanIncodeStatus();
    cleanDownloadPdf();
  };

  const navigateToAssignmentOrder = () => {
    navigate('/line-assignments/order-page', {
      state: {
        isProcessOrder: true,
        legal_id: legalId,
        order_id: orderId,
        searchFormData: navigationState?.searchFormData,
        lines,
      },
    });
    cleanAddLine();
    cleanRemoveLine();
    cleanDownloadPdf();
  };

  useEffect(() => {
    if (navigationState) {
      setValue('rfc', navigationState?.legal_id || '');
      setValue('legalName', navigationState?.legalName || '');
    }
  }, [navigationState, setValue]);

  return {
    dataValidation,
    register,
    errors,
    addLineForm,
    handleSubmit,
    loading,
    lines,
    isDialogOpen,
    isIncodeStatusDialogLocked,
    dialogTitleMessage,
    dialogMessage,
    handleClose,
    icon,
    aditionalInfoDialog,
    generateIncodeLinks,
    isIncodeDialogOpen,
    incodeLinksDialog,
    handleCloseIncodeDialog,
    incodeLinksLoading,
    handleDeleteLine,
    isRemoveDialogOpen,
    handleCloseRemoveDialog,
    handleConfirmRemoveLine,
    pendingRemoveLine,
    formatPhoneNumber,
    navigateToAssignmentOrder,
    getIncodeStatusExecute,
    setValue,
  };
};
